import React from 'react';
import PropTypes from 'prop-types';
import {observer} from 'mobx-react';
import {observable, makeObservable} from 'mobx';

import FormLabel from './form-label';

@observer
export default class TextInput extends React.Component {

  static propTypes = {
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    required: PropTypes.bool,
    charLimit: PropTypes.number,
    disabled: PropTypes.bool,
    labelText: PropTypes.string.isRequired,
    additionalSrText: PropTypes.string,
    helperText: PropTypes.string,
    labelIsSrOnly: PropTypes.bool,
    value: PropTypes.string,
    updateValue: PropTypes.func.isRequired,
    errorMessage: PropTypes.string,
    className: PropTypes.string,
    getIsValid: PropTypes.func,
    format: PropTypes.shape({
      criterion: PropTypes.obj,
      error: PropTypes.string
    }),
    onBlur: PropTypes.func,
    disableAutoComplete: PropTypes.bool,
    showErrorDirectiveFromMaster: PropTypes.bool
  }

  static defaultProps = {
    labelText: '',
    helperText: '',
    labelIsSrOnly: false,
    required: false,
    disabled: false,
    errorMessage: 'This entry is not valid.',
    className: '',
    disableAutoComplete: false,
    showErrorDirectiveFromMaster: false
  }

  constructor(props) {
    super(props);
    makeObservable(this);
    this.FORMAT_CRITERION = this.props.format ? this.props.format.criterion : '';
  }

  componentDidMount() {
    if(this.props.required && this.props.showErrorDirectiveFromMaster === true) {
      this.hasVisibleError = this.hasFunctionalError;
    }

    //On App Control only, do initial validation if there are format criteria
    if(!this.inputIsEmptyOrSpaces && this.breaksFormatCriterion) {
      this.hasVisibleError = this.hasFunctionalError;
    }
  }

  componentDidUpdate(prevProps) {
    if(prevProps.showErrorDirectiveFromMaster !== this.props.showErrorDirectiveFromMaster
      && this.props.showErrorDirectiveFromMaster === true) {
      this.hasVisibleError = this.hasFunctionalError;
    }
    if (prevProps.required !== this.props.required) {
        this.hasVisibleError = this.hasFunctionalError;
    }
  }

  @observable charLimitReached = false;
  @observable hasVisibleError = false;

  get inputIsEmptyOrSpaces() {
    return !this.props.value || !/\w+/.test(this.props.value);
  }

  get isMissing() {
    return this.props.required && this.inputIsEmptyOrSpaces;
  }
  get breaksFormatCriterion() {
    if(!this.inputIsEmptyOrSpaces && this.FORMAT_CRITERION) {
      return !new RegExp(this.FORMAT_CRITERION).test(this.props.value);
    } else {
      return false;
    }
  }
  get failsExtraValidation() {
    return this.props.getIsValid !== undefined && !this.props.getIsValid(this.props.value);
  }
  get hasFunctionalError() {
    return this.isMissing || this.breaksFormatCriterion || this.failsExtraValidation;
  }

  handleOnChange = (e) => {
    const newValue = e.target.value;
    if (this.props.charLimit) {
      this.props.updateValue(newValue.substr(0, this.props.charLimit));
      this.charLimitReached = newValue.length >= this.props.charLimit;
    } else {
      this.props.updateValue(newValue);
    }
  }

  handleOnKeyUp = (event) => {
    const actionsToExcludeFromKeyUp = ['Tab', 'Shift', 'Enter']; //Allow backspace; for example, if the user is deleting ".com" from a website, show an error.
    if(actionsToExcludeFromKeyUp.includes(event.key)) {
      /* FPSE-2316 - as part of this, don't clear the error simply because
      a user entered the field, wait for them do to something that changes
      the content. */
      return
    }
    //This handler catches focusing and typing actions.
    if(this.props.format === undefined) {
      //FPSE-1997 On App Control only, if the field had an error but has changed, remove error class
      this.hasVisibleError = false;
    } else {
      //On App Control only, live-validate IFF there are format criteria
      this.hasVisibleError = this.hasFunctionalError;
    }
  }

  handleOnBlur = () => {
    // On App Control only, do not validate on blur
    // this.hasVisibleError = this.hasFunctionalError;

    if(this.props.onBlur) {
      this.props.onBlur();
    }
  }

  focus() {
    this.input.focus();
  }

  render() {
    const Tag = this.props.type === 'textarea' ? 'textarea' : 'input';
    //In App Control only, the malformed error message is included in the errorMessage prop.
    return (
      <div className={`form-group ${this.props.className} ${this.hasVisibleError ? 'has-error' : ''}`}>
        <FormLabel
          htmlFor={this.props.id}
          charLimitReached={this.charLimitReached}
          hasError={this.hasVisibleError}
          fieldIsRequired={this.props.required}
          labelText={this.props.labelText}
          additionalSrText={this.props.additionalSrText}
          srOnly={this.props.labelIsSrOnly}
          helperText={this.props.helperText}
          errorMessage={this.props.errorMessage} />
        <div className="input-group">
          <Tag
            className="form-control"
            ref={(i) => {this.input = i}}
            id={this.props.id}
            type={this.props.type}
            disabled={this.props.disabled}
            onChange={this.handleOnChange}
            onKeyUp={this.handleOnKeyUp}
            onBlur={this.handleOnBlur}
            value={this.props.value}
            aria-invalid={this.hasVisibleError}
            aria-describedby={this.hasVisibleError ? this.props.id + 'Error' : ''}
            autoComplete={this.props.disableAutoComplete ? 'off' : 'on'}/>
        </div>
      </div>
    )
  }
}
